<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-body">
            <!-- Tabs -->
            <b-tabs v-model="activeTab" nav-class="nav-tabs-custom" content-class="pt-3">
              <!-- ============================== -->
              <!-- TAB 1: CAMPOS                  -->
              <!-- ============================== -->
              <b-tab title="Campos" active>
                <!-- Header -->
                <div class="d-flex justify-content-between align-items-center mb-4">
                  <div class="d-flex align-items-center gap-3">
                    <div class="search-box">
                      <i class="bx bx-search search-icon"></i>
                      <input
                        v-model="search"
                        type="text"
                        class="form-control"
                        placeholder="Buscar campos..."
                        @input="onSearch"
                      />
                    </div>
                    <select v-model="filterCategory" class="form-control" style="width: 200px" @change="loadTemplates">
                      <option value="">Todas as categorias</option>
                      <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                        {{ cat.name }}
                      </option>
                    </select>
                  </div>
                  <div class="d-flex gap-2">
                    <button class="btn btn-outline-primary" @click="showCategoryModal = true">
                      <i class="bx bx-folder-plus mr-1"></i> Categorias
                    </button>
                    <button class="btn btn-primary" @click="newTemplate">
                      <i class="bx bx-plus mr-1"></i> Novo Campo
                    </button>
                  </div>
                </div>

                <!-- Loading -->
                <div v-if="loading" class="text-center py-5">
                  <b-spinner></b-spinner>
                  <p class="mt-2 text-muted">Carregando campos...</p>
                </div>

                <!-- Empty -->
                <div v-else-if="templates.length === 0" class="text-center py-5">
                  <i class="bx bx-folder-open display-4 text-muted"></i>
                  <h5 class="mt-3">Nenhum campo pré-pronto cadastrado</h5>
                  <p class="text-muted">Clique em "Novo Campo" para criar o primeiro</p>
                  <button class="btn btn-primary mt-2" @click="newTemplate">
                    <i class="bx bx-plus mr-1"></i> Novo Campo
                  </button>
                </div>

                <!-- Table -->
                <div v-else class="table-responsive">
                  <table class="table table-hover align-middle">
                    <thead class="table-light">
                      <tr>
                        <th style="width: 40px"></th>
                        <th>Nome do Campo</th>
                        <th>Categoria</th>
                        <th>Grupo</th>
                        <th>Obrigatório</th>
                        <th>Status</th>
                        <th style="width: 120px" class="text-center">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="template in templates" :key="template.id">
                        <td>
                          <div class="field-icon-box">
                            <i :class="template.icon || getFieldIcon(template.field_type ? template.field_type.name : 'text')"></i>
                          </div>
                        </td>
                        <td>
                          <strong>{{ template.label }}</strong>
                          <br />
                          <small class="text-muted">{{ template.name }}</small>
                        </td>
                        <td>
                          <span v-if="template.category" class="badge badge-soft-secondary">
                            {{ template.category.name }}
                          </span>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td>
                          <span v-if="template.group_key" class="badge badge-soft-primary">
                            {{ getGroupLabel(template.group_key) }}
                          </span>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td>
                          <span v-if="template.required" class="badge badge-soft-danger">Sim</span>
                          <span v-else class="badge badge-soft-secondary">Não</span>
                        </td>
                        <td>
                          <b-form-checkbox
                            v-model="template.is_active"
                            switch
                            @change="toggleStatus(template)"
                          ></b-form-checkbox>
                        </td>
                        <td class="text-center">
                          <button
                            class="btn btn-sm btn-soft-primary mr-1"
                            @click="editTemplate(template)"
                            title="Editar"
                          >
                            <i class="bx bx-edit-alt"></i>
                          </button>
                          <button
                            class="btn btn-sm btn-soft-danger"
                            @click="confirmDelete(template)"
                            title="Excluir"
                          >
                            <i class="bx bx-trash"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Pagination -->
                <div v-if="pagination.total > pagination.perPage" class="d-flex justify-content-between align-items-center mt-4">
                  <p class="text-muted mb-0">
                    Mostrando {{ templates.length }} de {{ pagination.total }} campos
                  </p>
                  <b-pagination
                    v-model="pagination.currentPage"
                    :total-rows="pagination.total"
                    :per-page="pagination.perPage"
                    @change="onPageChange"
                  ></b-pagination>
                </div>
              </b-tab>

              <!-- ============================== -->
              <!-- TAB 2: GRUPOS                  -->
              <!-- ============================== -->
              <b-tab title="Grupos" @click="onGroupsTabClick">
                <!-- Header -->
                <div class="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <span class="text-muted">Gerencie grupos de campos que são adicionados juntos ao formulário</span>
                  </div>
                  <button class="btn btn-primary" @click="newGroup">
                    <i class="bx bx-plus mr-1"></i> Novo Grupo
                  </button>
                </div>

                <!-- Groups Loading -->
                <div v-if="groupsLoading" class="text-center py-5">
                  <b-spinner></b-spinner>
                  <p class="mt-2 text-muted">Carregando grupos...</p>
                </div>

                <!-- Empty Groups -->
                <div v-else-if="groups.length === 0" class="text-center py-5">
                  <i class="bx bx-collection display-4 text-muted"></i>
                  <h5 class="mt-3">Nenhum grupo cadastrado</h5>
                  <p class="text-muted">Clique em "Novo Grupo" para criar o primeiro</p>
                  <button class="btn btn-primary mt-2" @click="newGroup">
                    <i class="bx bx-plus mr-1"></i> Novo Grupo
                  </button>
                </div>

                <!-- Groups List -->
                <div v-else class="groups-list">
                  <div
                    v-for="group in groups"
                    :key="group.id"
                    class="group-card"
                  >
                    <!-- Group Header -->
                    <div class="group-card-header" @click="toggleGroupExpand(group.id)">
                      <div class="d-flex align-items-center gap-2">
                        <i :class="[group.icon || 'bx bx-group', 'group-icon']"></i>
                        <strong>{{ group.label }}</strong>
                        <span class="badge badge-soft-secondary ml-2">{{ group.templates_count || 0 }} campos</span>
                        <span v-if="group.category" class="badge badge-soft-primary ml-1">{{ group.category.name }}</span>
                      </div>
                      <div class="d-flex align-items-center gap-1" @click.stop>
                        <button class="btn btn-sm btn-soft-primary" @click="startEditGroup(group)" title="Editar grupo">
                          <i class="bx bx-edit-alt"></i>
                        </button>
                        <button class="btn btn-sm btn-soft-danger" @click="confirmDeleteGroup(group)" title="Excluir grupo">
                          <i class="bx bx-trash"></i>
                        </button>
                        <i :class="['bx', expandedGroups[group.id] ? 'bx-chevron-up' : 'bx-chevron-down', 'ml-2']" style="font-size: 20px; cursor: pointer" @click="toggleGroupExpand(group.id)"></i>
                      </div>
                    </div>

                    <!-- Group Body (expandible) -->
                    <div v-if="expandedGroups[group.id]" class="group-card-body">
                      <!-- Group Templates (draggable) -->
                      <draggable
                        v-if="group.groupTemplates && group.groupTemplates.length > 0"
                        v-model="group.groupTemplates"
                        handle=".drag-handle"
                        @end="onGroupReorder(group)"
                        class="group-templates-list"
                      >
                        <div
                          v-for="(tpl, idx) in group.groupTemplates"
                          :key="tpl.id"
                          class="group-template-item"
                        >
                          <span class="drag-handle"><i class="bx bx-menu"></i></span>
                          <span class="tpl-order">{{ idx + 1 }}</span>
                          <span class="tpl-label">{{ tpl.label }}</span>
                          <span class="badge badge-soft-secondary tpl-type">{{ tpl.field_type ? tpl.field_type.name : '-' }}</span>
                          <select
                            class="form-control form-control-sm tpl-cols"
                            :value="getTemplateCols(tpl)"
                            @change="onColsChange(group, tpl, $event.target.value)"
                          >
                            <option value="">auto</option>
                            <option value="3">col-3</option>
                            <option value="4">col-4</option>
                            <option value="6">col-6</option>
                            <option value="8">col-8</option>
                            <option value="12">col-12</option>
                          </select>
                          <button class="btn btn-sm btn-soft-danger" @click="removeTemplateFromGroup(group, tpl)" title="Remover do grupo">
                            <i class="bx bx-x"></i>
                          </button>
                        </div>
                      </draggable>

                      <div v-else class="text-center py-3 text-muted">
                        <small>Nenhum campo neste grupo</small>
                      </div>

                      <!-- Add Template to Group -->
                      <div class="group-add-template mt-2">
                        <div class="d-flex gap-2">
                          <select
                            v-model="addTemplateSelection[group.id]"
                            class="form-control form-control-sm"
                          >
                            <option :value="null">Selecionar campo...</option>
                            <option
                              v-for="tpl in getAvailableTemplatesForGroup(group)"
                              :key="tpl.id"
                              :value="tpl.id"
                            >
                              {{ tpl.label }} ({{ tpl.name }})
                            </option>
                          </select>
                          <button
                            class="btn btn-sm btn-outline-primary"
                            @click="addTemplateToGroup(group)"
                            :disabled="!addTemplateSelection[group.id]"
                          >
                            <i class="bx bx-plus"></i> Adicionar
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </b-tab>
            </b-tabs>
          </div>
        </div>
      </div>
    </div>

    <!-- Template Form Modal -->
    <b-modal
      v-model="showFormModal"
      :title="modalTitle"
      size="lg"
      :hide-footer="!isEditingMode"
      :ok-title="isEditingMode ? 'Salvar' : ''"
      :cancel-title="isEditingMode ? 'Cancelar' : ''"
      @ok="saveTemplate"
      @hidden="resetForm"
      centered
      body-class="p-0"
    >
      <!-- STEP 1: Select Field Type (only for new templates) -->
      <div v-if="!editingTemplate.field_type_id && !editingTemplate.id" class="template-wizard">
        <!-- Search Field Types -->
        <div class="wizard-search">
          <i class="bx bx-search"></i>
          <input
            v-model="fieldTypeSearch"
            type="text"
            placeholder="Buscar tipo de campo..."
            class="form-control"
          />
        </div>

        <!-- Field Types Grid -->
        <div class="field-types-grid">
          <div
            v-for="fieldType in filteredFieldTypes"
            :key="fieldType.id"
            class="field-type-card"
            @click="selectFieldType(fieldType)"
          >
            <div class="field-type-icon">
              <i :class="getFieldTypeIcon(fieldType)"></i>
            </div>
            <div class="field-type-info">
              <h6>{{ fieldType.label }}</h6>
              <p>{{ getCategoryLabel(fieldType.category) }}</p>
            </div>
            <i class="bx bx-chevron-right"></i>
          </div>
        </div>

        <div v-if="filteredFieldTypes.length === 0" class="empty-state-wizard">
          <i class="bx bx-search-alt"></i>
          <p>Nenhum tipo de campo encontrado</p>
        </div>
      </div>

      <!-- STEP 2: Configure Field -->
      <div v-else class="template-config">
        <!-- Header with field type -->
        <div v-if="selectedFieldType" class="config-header-bar">
          <button v-if="!editingTemplate.id" type="button" class="btn-back" @click="clearFieldType">
            <i class="bx bx-arrow-back"></i>
          </button>
          <div class="selected-type-info">
            <i :class="getFieldTypeIcon(selectedFieldType)"></i>
            <span>{{ selectedFieldType.label }}</span>
          </div>
        </div>

        <!-- Configuration Form -->
        <div class="config-form-body">
          <div class="form-group">
            <label>Categoria do Template *</label>
            <select v-model="editingTemplate.category_id" class="form-control">
              <option value="">Selecione uma categoria...</option>
              <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                {{ cat.name }}
              </option>
            </select>
            <small class="text-muted">Organize seus campos em categorias</small>
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>Label do Campo *</label>
                <input
                  v-model="editingTemplate.label"
                  type="text"
                  class="form-control"
                  placeholder="Ex: E-mail Corporativo"
                  @input="generateName"
                />
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>Identificador (name)</label>
                <input
                  v-model="editingTemplate.name"
                  type="text"
                  class="form-control"
                  placeholder="email_corporativo"
                />
                <small class="text-muted">Gerado automaticamente se vazio</small>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>Placeholder</label>
                <input
                  v-model="editingTemplate.placeholder"
                  type="text"
                  class="form-control"
                  placeholder="Digite aqui..."
                />
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>Ícone (opcional)</label>
                <input
                  v-model="editingTemplate.icon"
                  type="text"
                  class="form-control"
                  placeholder="bx bx-envelope"
                />
                <small class="text-muted">Formato: bx bx-[nome]</small>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label>Texto de Ajuda (opcional)</label>
            <textarea
              v-model="editingTemplate.help_text"
              class="form-control"
              rows="2"
              placeholder="Instruções adicionais para o usuário..."
            ></textarea>
          </div>

          <!-- Group Section (collapsible) -->
          <div class="group-section mb-3">
            <div class="group-section-header" @click="showGroupSection = !showGroupSection">
              <i :class="['bx', showGroupSection ? 'bx-chevron-down' : 'bx-chevron-right']"></i>
              <span>Grupo</span>
              <small v-if="editingTemplate.group_key" class="badge badge-soft-primary ml-2">{{ getGroupLabel(editingTemplate.group_key) }}</small>
            </div>
            <div v-if="showGroupSection" class="group-section-body mt-2">
              <div class="row">
                <div class="col-md-4">
                  <div class="form-group">
                    <label>Grupo</label>
                    <select v-model="editingTemplate.group_key" class="form-control">
                      <option :value="null">Nenhum</option>
                      <option value="">Nenhum</option>
                      <option v-for="g in groups" :key="g.id" :value="g.key">
                        {{ g.label }}
                      </option>
                    </select>
                  </div>
                </div>
                <div v-if="editingTemplate.group_key" class="col-md-4">
                  <div class="form-group">
                    <label>Ordem no Grupo</label>
                    <input
                      v-model.number="editingTemplate.group_order"
                      type="number"
                      class="form-control"
                      min="0"
                    />
                  </div>
                </div>
                <div v-if="editingTemplate.group_key" class="col-md-4">
                  <div class="form-group">
                    <label>Colunas (Layout)</label>
                    <select v-model="editingTemplateCols" class="form-control">
                      <option value="">Auto</option>
                      <option value="3">3 colunas</option>
                      <option value="4">4 colunas</option>
                      <option value="6">6 colunas</option>
                      <option value="8">8 colunas</option>
                      <option value="12">12 colunas</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Options (conditional) -->
          <div v-if="fieldTypeHasOptions" class="form-group">
            <label class="d-flex justify-content-between align-items-center">
              <span>Opções <span class="text-danger">*</span></span>
              <button type="button" class="btn btn-sm btn-outline-primary" @click="addOption">
                <i class="bx bx-plus"></i> Adicionar
              </button>
            </label>
            <div class="options-config-list">
              <div
                v-for="(option, index) in editingTemplate.options"
                :key="index"
                class="option-config-item"
              >
                <span class="option-number">{{ index + 1 }}</span>
                <input
                  v-model="option.label"
                  type="text"
                  class="form-control"
                  placeholder="Nome da opção"
                  @input="updateOptionValue(index)"
                />
                <button
                  v-if="editingTemplate.options.length > 1"
                  type="button"
                  class="btn-remove-option-mini"
                  @click="removeOption(index)"
                  title="Remover"
                >
                  <i class="bx bx-x"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Checkboxes -->
          <div class="config-checkboxes">
            <div class="checkbox-group">
              <b-form-checkbox v-model="editingTemplate.required">
                <i class="bx bx-error-circle text-danger"></i>
                Campo obrigatório
              </b-form-checkbox>
            </div>
            <div class="checkbox-group">
              <b-form-checkbox v-model="editingTemplate.unique">
                <i class="bx bx-check-shield text-info"></i>
                Valor único
              </b-form-checkbox>
            </div>
            <div class="checkbox-group">
              <b-form-checkbox v-model="editingTemplate.is_active">
                <i class="bx bx-toggle-right text-success"></i>
                Campo ativo
              </b-form-checkbox>
            </div>
          </div>

          <!-- Actions -->
          <div class="config-actions-bar">
            <button type="button" class="btn btn-outline-secondary" @click="closeModal">
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="saveTemplate"
              :disabled="!canSave"
            >
              <i class="bx bx-check mr-1"></i>
              {{ editingTemplate.id ? 'Salvar Alterações' : 'Criar Campo' }}
            </button>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Category Management Modal -->
    <b-modal
      v-model="showCategoryModal"
      title="Gerenciar Categorias"
      size="md"
      hide-footer
      centered
    >
      <div class="mb-3">
        <div class="input-group">
          <input
            v-model="newCategoryName"
            type="text"
            class="form-control"
            placeholder="Nome da nova categoria"
          />
          <button class="btn btn-primary" @click="addCategory">
            <i class="bx bx-plus"></i> Adicionar
          </button>
        </div>
      </div>

      <div v-if="categories.length === 0" class="text-center py-4 text-muted">
        Nenhuma categoria cadastrada
      </div>

      <ul v-else class="list-group">
        <li
          v-for="cat in categories"
          :key="cat.id"
          class="list-group-item d-flex justify-content-between align-items-center"
        >
          <div v-if="editingCategory && editingCategory.id === cat.id">
            <input
              v-model="editingCategory.name"
              type="text"
              class="form-control form-control-sm"
              style="width: 200px"
              @keyup.enter="saveCategory"
              @keyup.esc="editingCategory = null"
            />
          </div>
          <div v-else>
            <i :class="cat.icon || 'bx bx-folder'" class="mr-2"></i>
            {{ cat.name }}
            <small class="text-muted ml-2">({{ cat.templates_count || 0 }} campos)</small>
          </div>
          <div>
            <button
              v-if="editingCategory && editingCategory.id === cat.id"
              class="btn btn-sm btn-success mr-1"
              @click="saveCategory"
            >
              <i class="bx bx-check"></i>
            </button>
            <button
              v-else
              class="btn btn-sm btn-outline-secondary mr-1"
              @click="startEditCategory(cat)"
            >
              <i class="bx bx-edit-alt"></i>
            </button>
            <button
              class="btn btn-sm btn-outline-danger"
              @click="confirmDeleteCategory(cat)"
            >
              <i class="bx bx-trash"></i>
            </button>
          </div>
        </li>
      </ul>
    </b-modal>

    <!-- Group Form Modal (Create / Edit) -->
    <b-modal
      v-model="showGroupFormModal"
      :title="editingGroup && editingGroup.id ? 'Editar Grupo' : 'Novo Grupo'"
      size="md"
      hide-footer
      centered
      body-class="p-0"
      @hidden="resetGroupForm"
    >
      <div v-if="editingGroup" class="template-config">
        <!-- Header -->
        <div class="config-header-bar">
          <div class="selected-type-info">
            <i :class="editingGroup.icon || 'bx bx-collection'" style="font-size: 22px"></i>
            <span>{{ editingGroup.id ? editingGroup.label : 'Configurar Grupo' }}</span>
          </div>
        </div>

        <!-- Form -->
        <div class="config-form-body">
          <div class="form-group">
            <label>Nome do Grupo *</label>
            <input
              v-model="editingGroup.label"
              type="text"
              class="form-control"
              placeholder="Ex: Endereço Completo"
              @input="onGroupLabelInput"
            />
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>Identificador (key) *</label>
                <input
                  v-model="editingGroup.key"
                  type="text"
                  class="form-control"
                  placeholder="endereco_completo"
                  :disabled="!!editingGroup.id"
                />
                <small class="text-muted">{{ editingGroup.id ? 'Não editável após criação' : 'Gerado automaticamente' }}</small>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>Ícone</label>
                <div class="d-flex align-items-center gap-2">
                  <input
                    v-model="editingGroup.icon"
                    type="text"
                    class="form-control"
                    placeholder="bx bx-map"
                  />
                  <div v-if="editingGroup.icon" class="group-icon-preview">
                    <i :class="editingGroup.icon"></i>
                  </div>
                </div>
                <small class="text-muted">Formato: bx bx-[nome]</small>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>Categoria</label>
                <select v-model="editingGroup.category_id" class="form-control">
                  <option :value="null">Nenhuma</option>
                  <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                    {{ cat.name }}
                  </option>
                </select>
                <small class="text-muted">Filtra os campos disponíveis</small>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>Ordem de exibição</label>
                <input
                  v-model.number="editingGroup.order"
                  type="number"
                  class="form-control"
                  min="0"
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="config-actions-bar">
            <button type="button" class="btn btn-outline-secondary" @click="showGroupFormModal = false">
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="saveGroup"
              :disabled="!editingGroup.label || !editingGroup.key"
            >
              <i class="bx bx-check mr-1"></i>
              {{ editingGroup.id ? 'Salvar Alterações' : 'Criar Grupo' }}
            </button>
          </div>
        </div>
      </div>
    </b-modal>
  </Layout>
</template>

<script>
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import draggable from 'vuedraggable'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import {
  listFieldTemplates,
  createFieldTemplate,
  updateFieldTemplate,
  deleteFieldTemplate,
  listTemplateCategories,
  createTemplateCategory,
  updateTemplateCategory,
  deleteTemplateCategory,
  listTemplateGroups,
  createTemplateGroup,
  updateTemplateGroup,
  deleteTemplateGroup,
  reorderGroupTemplates
} from '../services'

export default {
  name: 'FieldTemplatesIndex',
  components: {
    Layout,
    PageHeader,
    draggable
  },
  data () {
    return {
      title: 'Biblioteca de Campos',
      items: [
        { text: 'Form Builder', href: '/form-builder' },
        { text: 'Biblioteca de Campos', active: true }
      ],
      activeTab: 0,
      loading: false,
      templates: [],
      categories: [],
      search: '',
      filterCategory: '',
      pagination: {
        currentPage: 1,
        perPage: 20,
        total: 0
      },
      showFormModal: false,
      showCategoryModal: false,
      editingTemplate: this.getEmptyTemplate(),
      editingCategory: null,
      newCategoryName: '',
      searchTimeout: null,
      fieldTypeSearch: '',
      fieldTypes: [],
      selectedFieldType: null,
      showGroupSection: false,
      // Groups tab
      groups: [],
      groupsLoading: false,
      groupsLoaded: false,
      expandedGroups: {},
      editingGroup: null,
      showGroupFormModal: false,
      addTemplateSelection: {},
      allTemplatesForGroups: [],
      fieldIcons: {
        text: 'bx bx-text',
        textarea: 'bx bx-align-left',
        email: 'bx bx-envelope',
        phone: 'bx bx-phone',
        number: 'bx bx-hash',
        select: 'bx bx-chevron-down',
        multiselect: 'bx bx-list-check',
        checkbox: 'bx bx-checkbox-checked',
        radio: 'bx bx-radio-circle-marked',
        cpf: 'bx bx-id-card',
        cnpj: 'bx bx-building',
        cep: 'bx bx-map-pin',
        date: 'bx bx-calendar',
        file: 'bx bx-upload',
        image: 'bx bx-image'
      },
      categoryLabels: {
        basic: 'Campos Básicos',
        selection: 'Campos de Escolha',
        brazilian: 'Campos Brasileiros',
        datetime: 'Data e Hora',
        special: 'Campos Avançados',
        layout: 'Layout'
      }
    }
  },
  computed: {
    modalTitle () {
      if (this.editingTemplate.id) {
        return 'Editar Campo Pré-pronto'
      }
      if (this.editingTemplate.field_type_id) {
        return 'Configurar Campo'
      }
      return 'Selecione o Tipo de Campo'
    },
    isEditingMode () {
      return !!this.editingTemplate.id
    },
    filteredFieldTypes () {
      if (!this.fieldTypeSearch) {
        return this.fieldTypes
      }
      const searchLower = this.fieldTypeSearch.toLowerCase()
      return this.fieldTypes.filter(ft =>
        ft.label.toLowerCase().includes(searchLower) ||
        ft.name.toLowerCase().includes(searchLower) ||
        this.getCategoryLabel(ft.category).toLowerCase().includes(searchLower)
      )
    },
    fieldTypeHasOptions () {
      if (!this.selectedFieldType) return false
      if (typeof this.selectedFieldType.has_options === 'boolean') {
        return this.selectedFieldType.has_options
      }
      const optionTypes = ['select', 'multiselect', 'radio', 'checkbox']
      return optionTypes.includes(this.selectedFieldType.name)
    },
    canSave () {
      return !!(
        this.editingTemplate.category_id &&
        this.editingTemplate.label &&
        this.editingTemplate.field_type_id
      )
    },
    editingTemplateCols: {
      get () {
        return (this.editingTemplate.layout && this.editingTemplate.layout.cols) || ''
      },
      set (val) {
        if (val) {
          this.$set(this.editingTemplate, 'layout', { ...(this.editingTemplate.layout || {}), cols: val })
        } else {
          this.$set(this.editingTemplate, 'layout', null)
        }
      }
    }
  },
  async mounted () {
    await this.loadCategories()
    await this.loadFieldTypes()
    await this.loadTemplates()
  },
  methods: {
    getEmptyTemplate () {
      return {
        id: null,
        category_id: '',
        field_type_id: null,
        label: '',
        name: '',
        placeholder: '',
        help_text: '',
        icon: '',
        required: false,
        unique: false,
        is_active: true,
        has_options: false,
        options: [],
        group_key: null,
        group_order: 0,
        layout: null
      }
    },

    async loadTemplates () {
      this.loading = true
      try {
        const params = {
          page: this.pagination.currentPage,
          per_page: this.pagination.perPage,
          search: this.search,
          category_id: this.filterCategory
        }
        const data = await listFieldTemplates(params)

        let templates = []
        let total = 0

        if (data) {
          if (Array.isArray(data)) {
            templates = data
            total = templates.length
          } else if (data.data && Array.isArray(data.data)) {
            templates = data.data
            total = (data.meta && data.meta.total) || data.total || templates.length
          }
        }

        this.templates = templates.map(t => ({
          ...t,
          field_type: t.field_type || null,
          category: t.category || null
        }))
        this.pagination.total = total
      } catch (err) {
        console.error(err)
        errorMessage('Erro ao carregar campos')
        this.templates = []
      } finally {
        this.loading = false
      }
    },

    async loadCategories () {
      try {
        const data = await listTemplateCategories()
        if (Array.isArray(data)) {
          this.categories = data
        } else if (data && data.data && Array.isArray(data.data)) {
          this.categories = data.data
        } else {
          this.categories = []
        }
      } catch (err) {
        console.error(err)
        this.categories = []
      }
    },

    async loadFieldTypes () {
      try {
        const { getFieldTypes } = await import('../services')
        const response = await getFieldTypes()
        if (response && response.data && Array.isArray(response.data)) {
          this.fieldTypes = response.data
        } else if (Array.isArray(response)) {
          this.fieldTypes = response
        } else {
          this.fieldTypes = []
        }
      } catch (err) {
        console.error('Error loading field types:', err)
        this.fieldTypes = []
      }
    },

    // ==========================================
    // GROUPS
    // ==========================================
    onGroupsTabClick () {
      if (!this.groupsLoaded) {
        this.loadGroups()
      }
    },

    async loadGroups () {
      this.groupsLoading = true
      try {
        const data = await listTemplateGroups()
        let groupsList = []
        if (data && data.data && Array.isArray(data.data)) {
          groupsList = data.data
        } else if (Array.isArray(data)) {
          groupsList = data
        }

        // Load templates for each group
        this.groups = groupsList.map(g => ({
          ...g,
          groupTemplates: g.templates || []
        }))
        this.groupsLoaded = true

        // Load all templates (for the "add to group" dropdown)
        await this.loadAllTemplatesForGroups()
      } catch (err) {
        console.error(err)
        errorMessage('Erro ao carregar grupos')
        this.groups = []
      } finally {
        this.groupsLoading = false
      }
    },

    async loadAllTemplatesForGroups () {
      try {
        const data = await listFieldTemplates({ all: true, is_active: true })
        if (data && data.data && Array.isArray(data.data)) {
          this.allTemplatesForGroups = data.data
        } else if (Array.isArray(data)) {
          this.allTemplatesForGroups = data
        } else {
          this.allTemplatesForGroups = []
        }
      } catch (err) {
        console.error(err)
        this.allTemplatesForGroups = []
      }
    },

    getGroupLabel (groupKey) {
      const group = this.groups.find(g => g.key === groupKey)
      return group ? group.label : groupKey
    },

    getEmptyGroup () {
      return {
        id: null,
        label: '',
        key: '',
        icon: '',
        category_id: null,
        order: 0
      }
    },

    newGroup () {
      this.editingGroup = this.getEmptyGroup()
      this.showGroupFormModal = true
    },

    startEditGroup (group) {
      this.editingGroup = { ...group }
      this.showGroupFormModal = true
    },

    onGroupLabelInput () {
      // Auto-generate key only for new groups
      if (this.editingGroup && !this.editingGroup.id && this.editingGroup.label) {
        this.editingGroup.key = this.slugify(this.editingGroup.label)
      }
    },

    resetGroupForm () {
      this.editingGroup = null
    },

    async saveGroup () {
      if (!this.editingGroup || !this.editingGroup.label || !this.editingGroup.key) {
        errorMessage('Preencha o nome e o identificador do grupo')
        return
      }

      try {
        if (this.editingGroup.id) {
          await updateTemplateGroup(this.editingGroup.id, {
            label: this.editingGroup.label,
            icon: this.editingGroup.icon,
            category_id: this.editingGroup.category_id,
            order: this.editingGroup.order
          })
          successMessage('Grupo atualizado!')
        } else {
          await createTemplateGroup(this.editingGroup)
          successMessage('Grupo criado com sucesso!')
        }
        this.showGroupFormModal = false
        this.editingGroup = null
        await this.loadGroups()
      } catch (err) {
        console.error(err)
        const errMsg = (err.response && err.response.data && err.response.data.message) || err.message
        errorMessage('Erro ao salvar grupo: ' + errMsg)
      }
    },

    confirmDeleteGroup (group) {
      this.$bvModal.msgBoxConfirm(
        `Deseja excluir o grupo "${group.label}"? Os campos ficarão sem grupo.`,
        {
          title: 'Confirmar Exclusão',
          okVariant: 'danger',
          okTitle: 'Excluir',
          cancelTitle: 'Cancelar',
          centered: true
        }
      ).then(async confirmed => {
        if (confirmed) {
          try {
            await deleteTemplateGroup(group.id)
            successMessage('Grupo excluído!')
            await this.loadGroups()
          } catch (err) {
            errorMessage('Erro ao excluir grupo')
          }
        }
      })
    },

    toggleGroupExpand (groupId) {
      this.$set(this.expandedGroups, groupId, !this.expandedGroups[groupId])
      // Load group templates if expanding and not already loaded
      if (this.expandedGroups[groupId]) {
        const group = this.groups.find(g => g.id === groupId)
        if (group && (!group.groupTemplates || group.groupTemplates.length === 0) && group.templates_count > 0) {
          this.loadGroupTemplates(group)
        }
      }
    },

    async loadGroupTemplates (group) {
      // Templates should already be in the group from loadGroups
      // But if not, we can load them from allTemplatesForGroups
      if (!group.groupTemplates || group.groupTemplates.length === 0) {
        group.groupTemplates = this.allTemplatesForGroups
          .filter(t => t.group_key === group.key)
          .sort((a, b) => (a.group_order || 0) - (b.group_order || 0))
      }
    },

    getAvailableTemplatesForGroup (group) {
      // Return templates that are not in any group (or filter by same category)
      return this.allTemplatesForGroups.filter(t => {
        // Not already in a group
        if (t.group_key) return false
        // Optionally filter by group's category
        if (group.category_id && t.category_id !== group.category_id) return false
        return true
      })
    },

    async addTemplateToGroup (group) {
      const templateId = this.addTemplateSelection[group.id]
      if (!templateId) return

      try {
        const template = this.allTemplatesForGroups.find(t => t.id === templateId)
        if (!template) return

        const nextOrder = group.groupTemplates ? group.groupTemplates.length : 0

        await updateFieldTemplate(templateId, {
          ...template,
          group_key: group.key,
          group_order: nextOrder
        })

        // Update local state
        template.group_key = group.key
        template.group_order = nextOrder
        if (!group.groupTemplates) {
          this.$set(group, 'groupTemplates', [])
        }
        group.groupTemplates.push(template)
        group.templates_count = (group.templates_count || 0) + 1

        this.$set(this.addTemplateSelection, group.id, null)
        successMessage('Campo adicionado ao grupo!')
      } catch (err) {
        console.error(err)
        errorMessage('Erro ao adicionar campo ao grupo')
      }
    },

    async removeTemplateFromGroup (group, template) {
      try {
        await updateFieldTemplate(template.id, {
          ...template,
          group_key: null,
          group_order: 0
        })

        // Update local state
        template.group_key = null
        template.group_order = 0
        const idx = group.groupTemplates.findIndex(t => t.id === template.id)
        if (idx !== -1) {
          group.groupTemplates.splice(idx, 1)
        }
        group.templates_count = Math.max(0, (group.templates_count || 0) - 1)

        successMessage('Campo removido do grupo!')
      } catch (err) {
        console.error(err)
        errorMessage('Erro ao remover campo do grupo')
      }
    },

    getTemplateCols (tpl) {
      return (tpl.layout && tpl.layout.cols) || ''
    },

    async onColsChange (group, tpl, value) {
      const layout = value ? { cols: value } : null
      try {
        await updateFieldTemplate(tpl.id, {
          ...tpl,
          layout: layout
        })
        this.$set(tpl, 'layout', layout)
        successMessage('Colunas atualizadas!')
      } catch (err) {
        console.error(err)
        errorMessage('Erro ao atualizar colunas')
      }
    },

    async onGroupReorder (group) {
      try {
        const templates = group.groupTemplates.map((tpl, idx) => ({
          template_id: tpl.id,
          group_order: idx,
          layout: tpl.layout || null
        }))

        await reorderGroupTemplates(group.id, { templates })

        // Update local group_order
        group.groupTemplates.forEach((tpl, idx) => {
          tpl.group_order = idx
        })
      } catch (err) {
        console.error(err)
        errorMessage('Erro ao reordenar campos')
      }
    },

    // ==========================================
    // TEMPLATES (existing methods)
    // ==========================================
    onSearch () {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.pagination.currentPage = 1
        this.loadTemplates()
      }, 300)
    },

    onPageChange (page) {
      this.pagination.currentPage = page
      this.loadTemplates()
    },

    getFieldIcon (typeName) {
      return this.fieldIcons[typeName] || 'bx bx-text'
    },

    getCategoryLabel (category) {
      return this.categoryLabels[category] || category
    },

    getFieldTypeIcon (fieldType) {
      if (fieldType.icon && fieldType.icon.startsWith('bx')) {
        return fieldType.icon
      }
      return this.fieldIcons[fieldType.name] || 'bx bx-text'
    },

    selectFieldType (fieldType) {
      this.selectedFieldType = fieldType
      this.editingTemplate.field_type_id = fieldType.id
      if (fieldType.icon && !this.editingTemplate.icon) {
        this.editingTemplate.icon = fieldType.icon
      }
      if (this.fieldTypeHasOptions && this.editingTemplate.options.length === 0) {
        this.editingTemplate.options = [
          { label: 'Opção 1', value: 'opcao_1' }
        ]
      }
    },

    clearFieldType () {
      this.selectedFieldType = null
      this.editingTemplate.field_type_id = null
      this.fieldTypeSearch = ''
    },

    updateOptionValue (index) {
      const opt = this.editingTemplate.options[index]
      if (opt.label && !opt.value) {
        opt.value = this.slugify(opt.label)
      }
    },

    closeModal () {
      this.showFormModal = false
      this.resetForm()
    },

    newTemplate () {
      this.editingTemplate = this.getEmptyTemplate()
      this.selectedFieldType = null
      this.fieldTypeSearch = ''
      this.showGroupSection = false
      this.showFormModal = true
    },

    editTemplate (template) {
      const hasOptions = template.options && template.options.length > 0
      this.editingTemplate = {
        ...template,
        category_id: template.category_id || (template.category && template.category.id) || '',
        field_type_id: template.field_type_id || (template.field_type && template.field_type.id) || null,
        has_options: hasOptions,
        options: template.options ? [...template.options] : [],
        group_key: template.group_key || null,
        group_order: template.group_order || 0,
        layout: template.layout || null
      }
      if (template.field_type) {
        this.selectedFieldType = template.field_type
      }
      this.showGroupSection = !!template.group_key
      this.showFormModal = true

      // Ensure groups are loaded for the modal dropdown
      if (!this.groupsLoaded) {
        this.loadGroups()
      }
    },

    async saveTemplate (bvModalEvent) {
      if (bvModalEvent && bvModalEvent.preventDefault) {
        bvModalEvent.preventDefault()
      }

      if (!this.editingTemplate.label || !this.editingTemplate.category_id || !this.editingTemplate.field_type_id) {
        errorMessage('Preencha o label, categoria e tipo de campo')
        return
      }

      if (!this.editingTemplate.name) {
        this.generateName()
      }

      // Clean group_key if empty string
      if (this.editingTemplate.group_key === '') {
        this.editingTemplate.group_key = null
      }

      try {
        if (this.editingTemplate.id) {
          await updateFieldTemplate(this.editingTemplate.id, this.editingTemplate)
          successMessage('Campo atualizado com sucesso!')
        } else {
          await createFieldTemplate(this.editingTemplate)
          successMessage('Campo criado com sucesso!')
        }
        this.showFormModal = false
        this.loadTemplates()
        // Reload groups if they've been loaded (to reflect changes)
        if (this.groupsLoaded) {
          this.loadGroups()
        }
      } catch (err) {
        console.error(err)
        const errMsg = (err.response && err.response.data && err.response.data.message) || err.message
        errorMessage('Erro ao salvar: ' + errMsg)
      }
    },

    async toggleStatus (template) {
      try {
        await updateFieldTemplate(template.id, { is_active: template.is_active })
        successMessage('Status atualizado!')
      } catch (err) {
        template.is_active = !template.is_active
        errorMessage('Erro ao atualizar status')
      }
    },

    confirmDelete (template) {
      this.$bvModal.msgBoxConfirm(
        `Deseja excluir o campo "${template.label}"?`,
        {
          title: 'Confirmar Exclusão',
          okVariant: 'danger',
          okTitle: 'Excluir',
          cancelTitle: 'Cancelar',
          centered: true
        }
      ).then(async confirmed => {
        if (confirmed) {
          try {
            await deleteFieldTemplate(template.id)
            successMessage('Campo excluído!')
            this.loadTemplates()
          } catch (err) {
            errorMessage('Erro ao excluir')
          }
        }
      })
    },

    generateName () {
      if (this.editingTemplate.label) {
        this.editingTemplate.name = this.editingTemplate.label
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_|_$/g, '')
      }
    },

    addOption () {
      if (!this.editingTemplate.options) {
        this.editingTemplate.options = []
      }
      this.editingTemplate.options.push({ label: '', value: '' })
    },

    removeOption (index) {
      this.editingTemplate.options.splice(index, 1)
    },

    resetForm () {
      this.editingTemplate = this.getEmptyTemplate()
      this.selectedFieldType = null
      this.fieldTypeSearch = ''
      this.showGroupSection = false
    },

    slugify (text) {
      return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '')
    },

    // Category methods
    async addCategory () {
      if (!this.newCategoryName.trim()) return

      try {
        await createTemplateCategory({ name: this.newCategoryName.trim() })
        successMessage('Categoria criada!')
        this.newCategoryName = ''
        this.loadCategories()
      } catch (err) {
        errorMessage('Erro ao criar categoria')
      }
    },

    startEditCategory (cat) {
      this.editingCategory = { ...cat }
    },

    async saveCategory () {
      if (!this.editingCategory || !this.editingCategory.name.trim()) return

      try {
        await updateTemplateCategory(this.editingCategory.id, { name: this.editingCategory.name.trim() })
        successMessage('Categoria atualizada!')
        this.editingCategory = null
        this.loadCategories()
      } catch (err) {
        errorMessage('Erro ao atualizar categoria')
      }
    },

    confirmDeleteCategory (cat) {
      this.$bvModal.msgBoxConfirm(
        `Deseja excluir a categoria "${cat.name}"? Os campos desta categoria ficarão sem categoria.`,
        {
          title: 'Confirmar Exclusão',
          okVariant: 'danger',
          okTitle: 'Excluir',
          cancelTitle: 'Cancelar',
          centered: true
        }
      ).then(async confirmed => {
        if (confirmed) {
          try {
            await deleteTemplateCategory(cat.id)
            successMessage('Categoria excluída!')
            this.loadCategories()
            this.loadTemplates()
          } catch (err) {
            errorMessage('Erro ao excluir categoria')
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.search-box {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
}

.search-box input {
  padding-left: 36px;
  width: 250px;
}

.field-icon-box {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f4ff;
  border-radius: 8px;
  color: #556ee6;
}

.badge-soft-primary {
  background-color: rgba(85, 110, 230, 0.18);
  color: #556ee6;
}

.badge-soft-secondary {
  background-color: rgba(116, 120, 141, 0.18);
  color: #74788d;
}

.badge-soft-danger {
  background-color: rgba(244, 106, 106, 0.18);
  color: #f46a6a;
}

.btn-soft-primary {
  background-color: rgba(85, 110, 230, 0.18);
  color: #556ee6;
  border: none;
}

.btn-soft-primary:hover {
  background-color: #556ee6;
  color: #fff;
}

.btn-soft-danger {
  background-color: rgba(244, 106, 106, 0.18);
  color: #f46a6a;
  border: none;
}

.btn-soft-danger:hover {
  background-color: #f46a6a;
  color: #fff;
}

.gap-1 {
  gap: 4px;
}

.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.option-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.option-row input {
  flex: 1;
}

/* ============================================
   Group Section in Template Modal
   ============================================ */
.group-section {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  overflow: hidden;
}

.group-section-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  background: #f8f9fa;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #495057;
}

.group-section-header:hover {
  background: #f0f1f3;
}

.group-section-body {
  padding: 12px;
}

/* ============================================
   Groups Tab Styles
   ============================================ */
.group-icon-preview {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f4ff;
  border-radius: 8px;
  flex-shrink: 0;
}

.group-icon-preview i {
  font-size: 20px;
  color: #556ee6;
}

.groups-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.group-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

.group-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  background: #fff;
  cursor: pointer;
  transition: background 0.15s;
}

.group-card-header:hover {
  background: #f8f9fa;
}

.group-icon {
  font-size: 20px;
  color: #556ee6;
}

.group-card-body {
  padding: 12px 16px;
  background: #fafbfc;
  border-top: 1px solid #e9ecef;
}

.group-templates-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.group-template-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  transition: box-shadow 0.15s;
}

.group-template-item:hover {
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}

.drag-handle {
  cursor: grab;
  color: #adb5bd;
  font-size: 16px;
  display: flex;
  align-items: center;
}

.drag-handle:active {
  cursor: grabbing;
}

.tpl-order {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e9ecef;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #495057;
  flex-shrink: 0;
}

.tpl-label {
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  color: #344054;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tpl-type {
  flex-shrink: 0;
}

.tpl-cols {
  width: 90px;
  flex-shrink: 0;
}

.group-add-template {
  padding-top: 8px;
  border-top: 1px dashed #e9ecef;
}

/* ============================================
   Template Wizard Styles
   ============================================ */

/* Wizard Container */
.template-wizard {
  min-height: 400px;
  display: flex;
  flex-direction: column;
}

/* Search Bar */
.wizard-search {
  padding: 16px;
  border-bottom: 1px solid #e9ecef;
  position: relative;
}

.wizard-search > i {
  position: absolute;
  left: 28px;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  font-size: 18px;
  z-index: 1;
}

.wizard-search input {
  width: 100%;
  padding: 10px 36px 10px 40px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 13px;
  background: #f8f9fa;
  transition: all 0.15s;
}

.wizard-search input:focus {
  outline: none;
  border-color: #556ee6;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.1);
}

/* Field Types Grid */
.field-types-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
  padding: 16px;
  max-height: 450px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #d0d5dd transparent;
}

.field-types-grid::-webkit-scrollbar {
  width: 6px;
}

.field-types-grid::-webkit-scrollbar-track {
  background: transparent;
}

.field-types-grid::-webkit-scrollbar-thumb {
  background-color: #d0d5dd;
  border-radius: 3px;
}

.field-types-grid::-webkit-scrollbar-thumb:hover {
  background-color: #b0b5bd;
}

/* Field Type Card */
.field-type-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}

.field-type-card:hover {
  border-color: #556ee6;
  background: #f8f9ff;
  box-shadow: 0 2px 8px rgba(85, 110, 230, 0.12);
  transform: translateY(-1px);
}

.field-type-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f4ff;
  border-radius: 8px;
  flex-shrink: 0;
}

.field-type-icon i {
  font-size: 20px;
  color: #556ee6;
}

.field-type-info {
  flex: 1;
  min-width: 0;
}

.field-type-info h6 {
  margin: 0 0 2px 0;
  font-size: 14px;
  font-weight: 600;
  color: #344054;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.field-type-info p {
  margin: 0;
  font-size: 11px;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.field-type-card > i.bx-chevron-right {
  font-size: 20px;
  color: #adb5bd;
  flex-shrink: 0;
  transition: all 0.15s;
}

.field-type-card:hover > i.bx-chevron-right {
  color: #556ee6;
  transform: translateX(2px);
}

/* Template Configuration */
.template-config {
  display: flex;
  flex-direction: column;
  min-height: 400px;
}

/* Config Header Bar */
.config-header-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.config-header-bar button {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  color: #495057;
  transition: all 0.15s;
}

.config-header-bar button:hover {
  border-color: #556ee6;
  color: #556ee6;
  background: #fff;
}

.config-header-bar button i {
  font-size: 18px;
}

.selected-type-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #344054;
}

.selected-type-info i {
  font-size: 20px;
  color: #556ee6;
}

/* Config Form Body */
.config-form-body {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #d0d5dd transparent;
}

.config-form-body::-webkit-scrollbar {
  width: 6px;
}

.config-form-body::-webkit-scrollbar-track {
  background: transparent;
}

.config-form-body::-webkit-scrollbar-thumb {
  background-color: #d0d5dd;
  border-radius: 3px;
}

.config-form-body::-webkit-scrollbar-thumb:hover {
  background-color: #b0b5bd;
}

.config-form-body .form-group {
  margin-bottom: 18px;
}

.config-form-body .form-group:last-child {
  margin-bottom: 0;
}

.config-form-body label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 6px;
}

.config-form-body label small {
  display: block;
  font-weight: 400;
  color: #6c757d;
  margin-top: 2px;
}

.config-form-body .form-control,
.config-form-body .form-control-sm {
  font-size: 13px;
  border-color: #e9ecef;
  transition: all 0.15s;
}

.config-form-body .form-control:focus,
.config-form-body .form-control-sm:focus {
  border-color: #556ee6;
  box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.1);
}

/* Options Configuration List */
.options-config-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.option-config-item {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 6px;
}

.option-config-item .option-number {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #556ee6;
  color: #fff;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
}

.option-config-item .form-control,
.option-config-item .form-control-sm {
  flex: 1;
  font-size: 13px;
  background: #fff;
}

.option-config-item button,
.option-config-item .btn-remove-option-mini {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #fee2e2;
  color: #dc2626;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;
}

.option-config-item button:hover,
.option-config-item .btn-remove-option-mini:hover {
  background: #fecaca;
}

.option-config-item button i,
.option-config-item .btn-remove-option-mini i {
  font-size: 16px;
}

.options-config-list > button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px;
  border: 1px dashed #d0d5dd;
  background: transparent;
  border-radius: 6px;
  font-size: 13px;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.15s;
}

.options-config-list > button:hover {
  border-color: #556ee6;
  color: #556ee6;
  background: #f8f9ff;
}

/* Config Checkboxes */
.config-checkboxes {
  display: flex;
  flex-direction: row;
  gap: 20px;
  flex-wrap: wrap;
}

.config-checkboxes .checkbox-group {
  display: flex;
  align-items: center;
}

.config-checkboxes .custom-control {
  display: flex;
  align-items: center;
  gap: 6px;
}

.config-checkboxes .custom-control-label {
  display: flex !important;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  margin-bottom: 0 !important;
  font-size: 13px;
  color: #495057;
  font-weight: 400;
}

.config-checkboxes .custom-control-label i {
  font-size: 16px;
}

/* Config Actions Bar (Footer) */
.config-actions-bar {
  display: flex;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid #e9ecef;
  background: #fff;
}

.config-actions-bar button {
  flex: 1;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.config-actions-bar button:first-child {
  border: 1px solid #e9ecef;
  background: #fff;
  color: #495057;
}

.config-actions-bar button:first-child:hover {
  border-color: #d0d5dd;
  background: #f8f9fa;
}

.config-actions-bar button:last-child {
  border: none;
  background: #556ee6;
  color: #fff;
}

.config-actions-bar button:last-child:hover:not(:disabled) {
  background: #485ec4;
}

.config-actions-bar button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Empty State Wizard */
.empty-state-wizard {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.empty-state-wizard i {
  font-size: 48px;
  color: #d0d5dd;
  margin-bottom: 12px;
}

.empty-state-wizard p {
  margin: 0;
  font-size: 14px;
  color: #9ca3af;
}
</style>
